import { Injectable } from '@angular/core';
import { CitationFeModel, FeMessageModel } from './models/fe-message.model';
import { ChatType } from './models/chat-type';
import {
  ApiErrorResponseModel,
  AssistantResponseModel, AssistantUpdateType,
  ChatResponseModel,
  CompanyChatResponseModel,
  ErrorType,
  MessageModel,
  MessageRole
} from 'digiteq-ai-portal-client-lib';
import { ThreadMessageModel } from 'digiteq-ai-portal-client-lib/model/threadMessageModel';
import { Subject } from 'rxjs';
import { ErrorService } from './services/error.service';
import { FormControl } from '@angular/forms';
import { AiAssistantService } from './services/ai-assistant.service';

@Injectable({
  providedIn: 'root'
})
export class ChatViewService {
  get messages(): FeMessageModel[] {
    return this._messages;
  }

  set messages(value: FeMessageModel[]) {
    this._messages = value;
    this.showWelcomeMessage = !value?.length;
  }

  chatType: ChatType;
  aiTypingResponse: boolean = false;
  aiResponseError = false;
  stopped = false;
  images: string[] = [];
  currentChatId: string | number | undefined;
  currentChatName: string | undefined;
  messageFileItems: File[] = [];
  messageCodeInterpreterFiles: File[] = [];
  assistantAttachments: string[];
  promptViolation = false;
  showWelcomeMessage = true;
  messageForm: FormControl;
  private _messages: FeMessageModel[] = [];
  processingFiles: boolean = false;

  messageSent$: Subject<void> = new Subject<void>();
  onChatResponseComplete$: Subject<void> = new Subject<void>();
  resend$: Subject<void> = new Subject<void>();


  constructor(
    private errorService: ErrorService,
    private aiAssistantService: AiAssistantService,
  ) {
  }

  isAssistant() {
    return this.chatType === ChatType.ASSISTANT;
  }

  getChatIdProperty() {
    return this.isAssistant() ? 'threadId' : 'chatId'
  }

  addMessage(newMessage: MessageModel | ThreadMessageModel) {
    const getFiles = () => {
      return this.messageFileItems.length
        ? this.messageFileItems?.map(f => f.name)
        : (newMessage as ThreadMessageModel).searchFiles?.map(f => f.name)
    }
    const getCodeFiles = () => {
      return this.messageCodeInterpreterFiles.length
        ? this.messageCodeInterpreterFiles?.map(f => f.name)
        : (newMessage as ThreadMessageModel).codeInterpreters?.map(f => f.name)
    }

    const currentMessages = this._messages;
    let assistantMessage: FeMessageModel;
    if (this.isAssistant()) {
      assistantMessage = {
        ...newMessage,
        files: getFiles(),
        codeFiles: getCodeFiles(),
      }
      currentMessages.push(assistantMessage);
    } else {
      currentMessages.push(newMessage)
    }
    this.messages = currentMessages;
  }

  private _firstMessagePart: boolean;

  handleChatResponse(response: { text: string, imageId?: string }, firstMessagePart: boolean) {
    this._firstMessagePart = firstMessagePart;
    if (response?.imageId) {
      this.images.push(response.imageId);
      response.text = `![ai-image](${ response.imageId })\n\n`;
    }
    const regex = /【(.*?)】/g;
    const test = regex.test(response.text);
    if (!test) {
      this.processResponse(response);
      if (this._messages.length > 1) {
        this._messages.forEach((m, index) => {
          if (m.role === MessageRole.Assistant && m.message === '\n```' && index < this._messages.length - 1) {
            this.messages = this._messages.filter((_, i) => i !== index);
          }
        })
      }
    }
    this.messageFileItems = [];
    this.messageCodeInterpreterFiles = [];
  }

  processAssistantResponse(response: AssistantResponseModel) {
    if (response.updateType === AssistantUpdateType.ProcessingFiles) {
      if (this.processingFiles) {
        this._messages[this._messages.length - 1].message = response.text;
      } else {
        this._messages.push({message: response.text, role: MessageRole.Assistant});
      }
      this.processingFiles = true;
      return;
    } else if (this.processingFiles) {
      this.processingFiles = false;
      this._messages.splice(-1);
      this._firstMessagePart = true;
    }
    if (response.updateType === AssistantUpdateType.StepEnd) {
      this._firstMessagePart = true;
    }
    if (response.updateType === AssistantUpdateType.Completed) {
      this._messages.splice(-1);
      return;
    }
    if (response.attachments) {
      response.text = '';
      this.assistantAttachments = response.attachments;
    }
  }

  processResponse(response: ChatResponseModel | CompanyChatResponseModel | AssistantResponseModel) {
    const citations: CitationFeModel[] = (response as CompanyChatResponseModel).citations;
    let messagePart = response.text;
    if (this.isAssistant()) {
      this.processAssistantResponse(response as AssistantResponseModel);
    }
    if (citations) {
      this.updateCitationIndexes(citations);
    }

    if (this._firstMessagePart) {
      const propertyId = this.getChatIdProperty()
      if (response[propertyId]) {
        this.currentChatId = response[propertyId];
        if (this.isAssistant()) {
          this.aiAssistantService.threadId = response[propertyId]
        }
      }
      messagePart = messagePart || '';
      this._messages.push({
        message: messagePart,
        citations,
        role: MessageRole.Assistant,
      });
    } else {
      const lastMessage = this._messages[this._messages.length - 1];
      lastMessage.message += messagePart;
      this.updateCitations(lastMessage);
    }

    this.aiResponseError = false;
    this.stopped = false;
    this.messageSent$.next();
  }

  handleChatStop() {
    this.aiTypingResponse = false;
    this.stopped = true;
    this.processingFiles = false;
  }

  handleChatError(err: ApiErrorResponseModel | string) {
    this.aiTypingResponse = false;
    if (err === 'Stream stopped') {
      this.stopped = true;
      return;
    }

    // remove last assistant's message client already started writing response
    if (this._messages[this._messages.length - 1].role === MessageRole.Assistant) {
      this._messages.splice(-1);
    }

    this.aiResponseError = true;
    this.processingFiles = false;

    let errDetail = 'Could not send your message.';
    if (typeof err !== 'string' && (err.type === ErrorType.ChatPromptError || err.type === ErrorType.ChatContentFilterError)) {
      errDetail = 'Your prompt is wrong or violates content rules. Please use a different prompt.'
      this.promptViolation = true;
    } else {
      this.promptViolation = false;
    }

    this.errorService.handleError(err, errDetail)
  }

  updateCitationIndexes(citations: CitationFeModel[]) {
    if (citations) {
      citations.forEach((c, index) => {
        c.index = index + 1;
        c.used = false;
      });
    }
  }

  updateCitations(model: FeMessageModel) {
    if (!model.citations) {
      return;
    }

    let citationIndexes: number[] = [];

    const spanRegex = /<sup\s+class=["']citation-in-text["']>([1-5])<\/sup>/g;
    let match: RegExpExecArray;

    // when chatting with OpenAI, after few messages, the engine starts to generate responses now with doc1 etc.,
    // but with formatted span with class "citation-in-text"
    while ((match = spanRegex.exec(model.message)) !== null) {
      citationIndexes.push(parseInt(match[1], 10));
    }

    // matches bracketed citation references, e.g. legacy "[doc1]" placeholders as well as
    // citations referenced directly by their (decoded) title, e.g. "[Moshi.docx]".
    // the negative lookahead avoids matching markdown links like "[text](url)".
    const citationRegex = /\[([^[\]]+)](?!\()/g;

    model.message = model.message.replace(citationRegex, (substring, reference) => {
      const docMatch = /^doc(\d+)$/.exec(reference);
      let citation: CitationFeModel | undefined;

      if (docMatch) {
        const idx = Number(docMatch[1]);
        citation = model.citations.find(c => c.index === idx);
      } else {
        const decodedReference = this.decodeHtmlEntities(reference).trim().toLowerCase();
        citation = model.citations.find(c => this.decodeHtmlEntities(c.title || '').trim().toLowerCase() === decodedReference);
      }

      if (!citation) {
        return substring;
      }

      citationIndexes.push(citation.index);
      return `<sup class="citation-in-text">${ citation.index }</sup>`;
    });

    citationIndexes = [...new Set(citationIndexes)];

    model.citations.filter(c => citationIndexes.includes(c.index))
      .forEach(c => {
        c.used = true;
        c.title = this.decodeHtmlEntities(c.title);
      });
  }

  decodeHtmlEntities(input: string): string {
    const doc = new DOMParser().parseFromString(input, "text/html");
    return doc.documentElement.textContent;
  }

  reset() {
    this.messages = [];
    this.currentChatId = undefined;
    this.aiAssistantService.threadSearchFiles = [...[]];
    this.aiAssistantService.threadCodeInterpreterFiles = [...[]];
    this.messageFileItems = [...[]];
    this.messageCodeInterpreterFiles = [...[]];
    if (this.messageForm) {
      this.messageForm.enable();
    }
  }
}
